import '../css/app.css';

import { createInertiaApp, router } from '@inertiajs/vue3';
import { showRestrictedAccess } from '@/composables/useRestrictedAccess';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { ZiggyVue } from 'ziggy-js';
import axios from 'axios';

const appName = import.meta.env.VITE_APP_NAME || 'AIM Inventory System';

axios.defaults.withCredentials = true;
axios.defaults.timeout = 180_000; // 3 minutes — allow large imports to complete

// Laravel sets XSRF-TOKEN on every response; axios sends it as X-XSRF-TOKEN automatically.
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 419) {
            console.warn('CSRF token mismatch detected. Refreshing page...');
            window.location.reload();
        }

        if (error.response?.status === 403) {
            showRestrictedAccess();
        }

        return Promise.reject(error);
    },
);

router.on('invalid', (event) => {
    if (event.detail.response?.status === 403) {
        event.preventDefault();
        showRestrictedAccess();
    }
});

router.on('error', (event) => {
    const status = event.detail.response?.status;

    if (status === 403) {
        showRestrictedAccess();
    }
});

window.axios = axios;

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: async (name) => {
        const page = await resolvePageComponent(`./pages/${name}.vue`, import.meta.glob<DefineComponent>('./pages/**/*.vue'));
        
        if (page.default.layout === 'app') {
            const AppLayout = (await import('./layouts/AppLayout.vue')).default;
            return Object.assign({}, page, {
                default: Object.assign({}, page.default, {
                    layout: AppLayout,
                }),
            });
        }

        if (page.default.layout === 'admin') {
            const AdminLayout = (await import('./layouts/AdminLayout.vue')).default;
            return Object.assign({}, page, {
                default: Object.assign({}, page.default, {
                    layout: AdminLayout,
                }),
            });
        }

        return page;
    },
    setup({ el, App, props, plugin }) {
        createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(ZiggyVue)
            .mount(el);
    },
    progress: {
        color: 'rgb(17, 112, 187)',
    },
});

